import type { LoaderFunctionArgs } from 'react-router-dom';
import { redirect } from 'react-router-dom';
import { store, type RootState } from '../store/store';
import { waitForRehydration } from '../store/waitForRehydration';
import {
  loadBlockStudy,
  loadBlockReview,
  loadFlashcardStudy,
  loadSessionSummary,
  forceRefreshBlocksAndVignettes,
} from '../helpers/studyHelpers';
import { examsSelectors } from '../slices/exams/examsSlice';
import { isPreclinicalModeExam } from '../utils/examUtils';
import { selectActiveBlock } from '../slices/blocks/blocksSlice';
import {
  enterStandalone,
  exitStandalone,
  selectStudyContext,
  selectStudyFlashcardScope,
} from '../slices/studyContext/studyContextSlice';
import {
  clearVignettes,
  selectVignettesBlockId,
  vignettesSelectors,
} from '../slices/vignettes/vignettesSlice';
import {
  selectActiveQuestionIndex,
  setActiveQuestionIndex,
} from '../slices/study/studySlice';
import { getScoringVignetteResponse } from '../utils/vignetteResponses';
import { BlockType } from '../types/Study';
import type { FlashcardRunScope } from '../slices/flashcardRun/flashcardRunSlice';
import {
  bootstrapSessionFlashcardRun,
  bootstrapStandaloneFlashcardRun,
} from '../services/flashcardRun';
import { getStudySet } from '../services/studySets';
import { getVideoById } from '../services/videos';
import { videosLoaded } from '../slices/videos/videosSlice';
import type { Video } from '../types/Video';
import {
  studySetVideoContainerId,
  studySetVideoIds,
} from '../components/ora/VideoStudy/studySetVideos';
import {
  isCustomPrimaryExam,
  shouldUseLectureData,
  getLectureIds,
  hasStaleTodaySessionExam,
  getBlockId,
  getFlashcardSetScope,
  getStudySetIdFromUrl,
  hasPreloadedBlockData,
  getStandaloneFlashcardScopeFromUrl,
  hasSessionFlashcardRunSource,
  runMatchesScope,
  getSessionFlashcardRunOptionsFromUrl,
} from './studyLoaderDecisions';

const clearVignettesIfBlockChanged = (
  state: RootState,
  nextBlockId?: string | null
) => {
  if (!nextBlockId) return;
  const currentBlockId = selectActiveBlock(state)?.id;
  if (currentBlockId && currentBlockId === nextBlockId) {
    return;
  }

  const vignetteCount = Array.isArray(state.vignettes?.ids)
    ? state.vignettes.ids.length
    : 0;

  if (vignetteCount > 0) {
    store.dispatch(clearVignettes());
  }
};

/**
 * The first question of the loaded block the student still has to answer.
 *
 * Answered is read against the block the vignettes were loaded for (ORA-465):
 * a repeat of a variant they answered somewhere else starts unanswered here.
 */
const firstUnansweredVignetteIndex = (state: RootState): number => {
  const blockId = selectVignettesBlockId(state);
  const activeBlock = selectActiveBlock(state);
  const vignettes = vignettesSelectors.selectAll(state);

  const index = vignettes.findIndex((vignette) => {
    const scoringResponse = getScoringVignetteResponse(vignette, blockId);

    const isAnswered =
      activeBlock?.type === BlockType.tutor
        ? Boolean(scoringResponse?.isSubmitted)
        : Boolean(scoringResponse?.answer);

    return !isAnswered;
  });

  if (index !== -1) return index;

  const activeQuestionIndex = selectActiveQuestionIndex(state);
  return activeQuestionIndex >= 0 && activeQuestionIndex < vignettes.length
    ? activeQuestionIndex
    : 0;
};

const safeRedirectHome = () => {
  throw redirect('/');
};

/**
 * A standalone block that failed to load must not leave the study context in
 * standalone mode: the mode is persisted, and the redirect home may not change
 * the pathname (Open pressed from the dashboard), so the route sync would never
 * clear it and the next Today session would retry the dead block (ORA-674).
 */
const redirectHomeAfterFailedLoad = () => {
  if (selectStudyContext(store.getState()).mode === 'standalone') {
    store.dispatch(exitStandalone());
  }
  safeRedirectHome();
};

export const blockStudyLoader = async ({ request }: LoaderFunctionArgs) => {
  await waitForRehydration();
  const state = store.getState();
  const url = new URL(request.url);

  try {
    const studyContext = selectStudyContext(state);
    if (isCustomPrimaryExam(state) && studyContext.mode !== 'standalone') {
      safeRedirectHome();
    }

    if (hasStaleTodaySessionExam(state)) {
      await forceRefreshBlocksAndVignettes();
      return null;
    }

    const blockId = getBlockId(url, state);
    clearVignettesIfBlockChanged(state, blockId);
    if (!hasPreloadedBlockData(state, blockId)) {
      await loadBlockStudy(blockId ?? undefined);
    }

    // Starting or resuming a block lands on the first question still to answer.
    // This loader only runs on route entry or reload, so it never moves a
    // student mid-block; blockReviewLoader remains on question 1.
    const loadedState = store.getState();
    const activeBlock = selectActiveBlock(loadedState);
    const vignettes = vignettesSelectors.selectAll(loadedState);
    if (!activeBlock?.isSubmitted && vignettes.length > 0) {
      store.dispatch(
        setActiveQuestionIndex(firstUnansweredVignetteIndex(loadedState))
      );
    }

    return null;
  } catch {
    redirectHomeAfterFailedLoad();
  }
};

export const blockReviewLoader = async ({ request }: LoaderFunctionArgs) => {
  await waitForRehydration();
  const state = store.getState();
  const url = new URL(request.url);
  const blockId = getBlockId(url, state);

  try {
    const studyContext = selectStudyContext(state);
    if (isCustomPrimaryExam(state) && studyContext.mode !== 'standalone') {
      safeRedirectHome();
    }

    if (hasStaleTodaySessionExam(state)) {
      await forceRefreshBlocksAndVignettes();
      return null;
    }

    clearVignettesIfBlockChanged(state, blockId);
    if (!hasPreloadedBlockData(state, blockId)) {
      await loadBlockReview(blockId ?? undefined);
    }
    return null;
  } catch {
    redirectHomeAfterFailedLoad();
  }
};

export const flashcardsLoader = async ({ request }: LoaderFunctionArgs) => {
  await waitForRehydration();
  const state = store.getState();
  const url = new URL(request.url);
  const setScope = getFlashcardSetScope(url);
  const urlStandaloneScope = getStandaloneFlashcardScopeFromUrl(url);

  try {
    // A set fixes both the cards and their order, so it is decided before every
    // other branch: none of the filter parsing below applies to one, and a
    // reload of the URL has to rebuild the same run (ORA-681).
    if (setScope) {
      if (!runMatchesScope(state, setScope)) {
        await bootstrapStandaloneFlashcardRun(setScope);
      }

      const studyContext = selectStudyContext(state);

      store.dispatch(
        enterStandalone({
          // A set-driven deck opens no QBank block.
          blockId: null,
          returnTo: studyContext.returnTo ?? 'home',
          returnPath: studyContext.returnPath,
          returnSearch: studyContext.returnSearch,
          flashcardScope: setScope,
          // Carried through a reload: the way back to the thread is the whole
          // reason the run knows it came from one.
          chatReturn: studyContext.chatReturn,
        })
      );

      return null;
    }

    const usesLectureData = shouldUseLectureData(url, state);
    const studyContext = selectStudyContext(state);
    const usesSessionFlashcardRun = hasSessionFlashcardRunSource(url);
    if (usesSessionFlashcardRun && studyContext.mode === 'standalone') {
      store.dispatch(exitStandalone());
    }

    const effectiveStudyMode = usesSessionFlashcardRun
      ? 'session'
      : studyContext.mode;

    if (
      isCustomPrimaryExam(state) &&
      effectiveStudyMode !== 'standalone' &&
      !usesLectureData &&
      !urlStandaloneScope
    ) {
      safeRedirectHome();
    }

    if (effectiveStudyMode === 'standalone' && !usesSessionFlashcardRun) {
      const standaloneScope =
        selectStudyFlashcardScope(state) ?? urlStandaloneScope;

      if (
        standaloneScope &&
        standaloneScope.kind !== 'session' &&
        !runMatchesScope(state, standaloneScope)
      ) {
        await bootstrapStandaloneFlashcardRun(standaloneScope);
      }
      if (
        standaloneScope &&
        standaloneScope.kind !== 'session' &&
        !selectStudyFlashcardScope(state)
      ) {
        store.dispatch(
          enterStandalone({
            blockId: 'flashcards',
            returnTo: studyContext.returnTo ?? 'home',
            returnPath: studyContext.returnPath,
            returnSearch: studyContext.returnSearch,
            flashcardScope: standaloneScope,
          })
        );
      }
      return null;
    }

    if (urlStandaloneScope) {
      if (!runMatchesScope(state, urlStandaloneScope)) {
        await bootstrapStandaloneFlashcardRun(urlStandaloneScope);
      }

      store.dispatch(
        enterStandalone({
          blockId: 'flashcards',
          returnTo: 'home',
          flashcardScope: urlStandaloneScope,
        })
      );

      return null;
    }

    if (usesLectureData) {
      const lectureIds = getLectureIds(url, state);
      if (lectureIds.length > 0) {
        const lectureScope: Extract<
          FlashcardRunScope,
          { kind: 'lecture' | 'filters' }
        > = {
          kind: 'lecture',
          lectureIds,
          amount: 'all',
          type: 'both',
        };

        if (!runMatchesScope(store.getState(), lectureScope)) {
          await bootstrapStandaloneFlashcardRun(lectureScope);
        }

        store.dispatch(
          enterStandalone({
            blockId: 'flashcards',
            returnTo: 'home',
            flashcardScope: lectureScope,
          })
        );
      }
      return null;
    }

    if (url.searchParams.get('source') === 'filters') {
      safeRedirectHome();
    }

    await loadFlashcardStudy();
    const nextState = store.getState();
    const sessionId = nextState.todaySession.session?.id;
    if (!sessionId) {
      safeRedirectHome();
    }

    const sessionScope: FlashcardRunScope = {
      kind: 'session',
      studySessionId: sessionId,
      ...getSessionFlashcardRunOptionsFromUrl(url),
    };

    if (!runMatchesScope(nextState, sessionScope)) {
      await bootstrapSessionFlashcardRun(sessionId, {
        amount: sessionScope.amount,
        count: sessionScope.count,
        type: sessionScope.type,
        order: sessionScope.order,
      });
    }
    return null;
  } catch {
    safeRedirectHome();
  }
};

/**
 * `/study/video`, which until now had no loader at all.
 *
 * It exists for one case: `?setId=`, where the playlist is the set's items
 * rather than the daily session's or a lecture's. Everything else falls through
 * untouched, so the route behaves exactly as it did.
 */
export const videoStudyLoader = async ({ request }: LoaderFunctionArgs) => {
  await waitForRehydration();
  const url = new URL(request.url);
  const setId = getStudySetIdFromUrl(url);
  if (!setId) return null;

  try {
    const set = await getStudySet(setId);
    const videoIds = studySetVideoIds(set);

    const videos = (
      await Promise.all(
        videoIds.map((videoId) =>
          // One dead video must not cost the student the rest of the playlist.
          getVideoById(videoId).catch(() => null)
        )
      )
    ).filter((video): video is Video => video !== null);

    store.dispatch(
      videosLoaded({ lectureId: studySetVideoContainerId(setId), videos })
    );

    const studyContext = selectStudyContext(store.getState());

    // Standalone is re-entered only for the set the student actually launched.
    // A `?setId=` on any other route — a shared link, a stale URL, a set that
    // belongs to a run they have already left — gets its playlist and nothing
    // else: forcing standalone there would throw away whatever session mode
    // and scope the student is really in.
    if (studyContext.chatReturn?.setId === setId) {
      store.dispatch(
        enterStandalone({
          blockId: null,
          returnTo: studyContext.returnTo ?? 'home',
          returnPath: studyContext.returnPath,
          returnSearch: studyContext.returnSearch,
          chatReturn: studyContext.chatReturn,
        })
      );
    }

    return null;
  } catch {
    redirectHomeAfterFailedLoad();
  }
};

export const sessionSummaryLoader = async () => {
  await waitForRehydration();
  const state = store.getState();
  const primaryExam = examsSelectors.selectPrimaryExam(state);
  try {
    if (isPreclinicalModeExam(primaryExam)) {
      safeRedirectHome();
    }
    await loadSessionSummary();
    return null;
  } catch {
    safeRedirectHome();
  }
};
